import { useCallback, useEffect, useSyncExternalStore } from "react";
import { armNotificationSoundUnlock } from "@/lib/notificationSound";

export interface NotificationPreferences {
  // Notification types to receive
  enableMentions: boolean;
  enableAssignments: boolean;
  enableStatusChanges: boolean;
  enableComments: boolean;
  enableAutomations: boolean;

  // Desktop notifications
  enableDesktopNotifications: boolean;
  desktopOnlyHighPriority: boolean; // Only mentions, assignments, automations

  // In-app notifications
  enableToastNotifications: boolean;
  enableSoundNotifications: boolean;

  // Grouping and display
  groupNotifications: boolean;
  showNotificationPreviews: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  // All notification types enabled by default
  enableMentions: true,
  enableAssignments: true,
  enableStatusChanges: true,
  enableComments: true,
  enableAutomations: true,

  // Desktop notifications enabled, high-priority only
  enableDesktopNotifications: true,
  desktopOnlyHighPriority: true,

  // In-app notifications enabled
  enableToastNotifications: true,
  enableSoundNotifications: false,

  // Grouping enabled
  groupNotifications: true,
  showNotificationPreviews: true,
};

const STORAGE_KEY = "forge-notification-preferences";

// ────────────────────────────────────────────────────────────────────────────
// Module-scoped store — several components mount their own instance of this
// hook at once (Settings page, the persistent NotificationCenter, the
// /notifications page). Plain per-instance useState left every instance but
// the one that called updatePreference() stuck on stale values until a
// remount/reload. A shared snapshot + subscriber list makes every mounted
// instance re-render immediately when any instance updates a preference.
// ────────────────────────────────────────────────────────────────────────────

function loadPreferences(): NotificationPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<NotificationPreferences>;
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch (error) {
    console.error("Failed to load notification preferences:", error);
  }
  return DEFAULT_PREFERENCES;
}

let currentPreferences: NotificationPreferences | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): NotificationPreferences {
  currentPreferences ??= loadPreferences();
  return currentPreferences;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setPreferencesStore(next: NotificationPreferences) {
  currentPreferences = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    console.error("Failed to save notification preferences:", error);
  }
  listeners.forEach((listener) => listener());
}

/**
 * Hook to manage user notification preferences.
 * Backed by a module-scoped store (see above) on top of localStorage.
 */
export function useNotificationPreferences() {
  const preferences = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_PREFERENCES);

  useEffect(() => {
    if (preferences.enableSoundNotifications) {
      armNotificationSoundUnlock();
    }
  }, [preferences.enableSoundNotifications]);

  // Kept for API compatibility with existing callers; the store now loads
  // synchronously on first read so there's no async "still loading" state.
  const isLoaded = true;

  const savePreferences = useCallback((newPreferences: NotificationPreferences) => {
    setPreferencesStore(newPreferences);
  }, []);

  const updatePreference = useCallback(
    <K extends keyof NotificationPreferences>(
      key: K,
      value: NotificationPreferences[K]
    ) => {
      setPreferencesStore({ ...getSnapshot(), [key]: value });
    },
    []
  );

  const resetPreferences = useCallback(() => {
    setPreferencesStore(DEFAULT_PREFERENCES);
  }, []);

  // Check if a notification type should be shown
  const shouldShowNotificationType = useCallback(
    (type: string | null): boolean => {
      if (!type) return true; // Unknown types are shown by default

      switch (type) {
        case "mention":
          return preferences.enableMentions;
        case "assignment":
          return preferences.enableAssignments;
        case "status_change":
          return preferences.enableStatusChanges;
        case "comment":
          return preferences.enableComments;
        case "automation":
          return preferences.enableAutomations;
        default:
          return true; // Unknown types are shown by default
      }
    },
    [preferences]
  );

  // Check if desktop notifications should be shown for a type
  const shouldShowDesktopNotification = useCallback(
    (type: string | null): boolean => {
      // Visitor intake alerts are in-app only, regardless of desktop opt-in.
      if (type === "forge_form_submission") return false;
      if (!preferences.enableDesktopNotifications) {
        return false;
      }

      if (!shouldShowNotificationType(type)) {
        return false;
      }

      // If desktop-only high-priority, filter to high-priority types
      if (preferences.desktopOnlyHighPriority) {
        const highPriorityTypes = ["mention", "assignment", "automation"];
        return type ? highPriorityTypes.includes(type) : false;
      }

      return true;
    },
    [preferences.enableDesktopNotifications, preferences.desktopOnlyHighPriority, shouldShowNotificationType]
  );

  // Check if toast notifications should be shown
  const shouldShowToastNotification = useCallback(
    (type: string | null): boolean => {
      if (!preferences.enableToastNotifications) {
        return false;
      }

      return shouldShowNotificationType(type);
    },
    [preferences.enableToastNotifications, shouldShowNotificationType]
  );

  // Check if a sound should play — independent of toast visibility, so
  // disabling toasts doesn't silently mute sound notifications for users who
  // enabled sound separately.
  const shouldPlaySoundNotification = useCallback(
    (type: string | null): boolean => {
      if (!preferences.enableSoundNotifications) {
        return false;
      }

      return shouldShowNotificationType(type);
    },
    [preferences.enableSoundNotifications, shouldShowNotificationType]
  );

  return {
    preferences,
    isLoaded,
    savePreferences,
    updatePreference,
    resetPreferences,
    shouldShowNotificationType,
    shouldShowDesktopNotification,
    shouldShowToastNotification,
    shouldPlaySoundNotification,
  };
}
